import { create } from 'zustand';
import { darePacks } from '../data/darePacks';
import type { DarePack } from '../data/darePacks';

export type DoOrDrinkPhase = 'setup' | 'playing' | 'result' | 'game_end';

export interface DoOrDrinkPlayer {
  name: string;
  daresCompleted: number;
  daresDrunk: number;
}

export interface DoOrDrinkConfig {
  targetDares: number;
  darePack: DarePack['id'];
}

interface DoOrDrinkStore {
  // State
  phase: DoOrDrinkPhase;
  players: DoOrDrinkPlayer[];
  currentPlayerIndex: number;
  currentDare: string | null;
  config: DoOrDrinkConfig;
  usedDareIndices: number[];
  darePool: string[];
  roundNumber: number;
  lastAction: 'completed' | 'skipped' | null;

  // Actions
  initGame: (playerNames: string[], config: DoOrDrinkConfig) => void;
  showNextDare: () => void;
  dareCompleted: () => void;
  dareSkipped: () => void;
  advancePlayer: () => void;
  resetGame: () => void;
}

const initialState = {
  phase: 'setup' as DoOrDrinkPhase,
  players: [] as DoOrDrinkPlayer[],
  currentPlayerIndex: 0,
  currentDare: null as string | null,
  config: {
    targetDares: 10,
    darePack: 'default' as DarePack['id'],
  },
  usedDareIndices: [] as number[],
  darePool: [] as string[],
  roundNumber: 0,
  lastAction: null as 'completed' | 'skipped' | null,
};

export const useDoOrDrinkStore = create<DoOrDrinkStore>((set, get) => ({
  ...initialState,

  initGame: (playerNames, config) => {
    const pack = darePacks[config.darePack];
    const players = playerNames.map((name) => ({
      name,
      daresCompleted: 0,
      daresDrunk: 0,
    }));

    set({
      phase: 'playing',
      players,
      currentPlayerIndex: 0,
      currentDare: null,
      config,
      usedDareIndices: [],
      darePool: [...pack.dares],
      roundNumber: 1,
      lastAction: null,
    });

    // Immediately show the first dare
    get().showNextDare();
  },

  showNextDare: () => {
    const { darePool, usedDareIndices } = get();
    const availableIndices = darePool
      .map((_, i) => i)
      .filter((i) => !usedDareIndices.includes(i));

    if (availableIndices.length === 0) {
      // All dares used, reset the pool
      const randomIndex = Math.floor(Math.random() * darePool.length);
      set({
        currentDare: darePool[randomIndex],
        usedDareIndices: [randomIndex],
        phase: 'playing',
        lastAction: null,
      });
      return;
    }

    const randomIdx = availableIndices[Math.floor(Math.random() * availableIndices.length)]!;
    set({
      currentDare: darePool[randomIdx] ?? null,
      usedDareIndices: [...usedDareIndices, randomIdx],
      phase: 'playing',
      lastAction: null,
    });
  },

  dareCompleted: () => {
    const { players, currentPlayerIndex, config } = get();
    const current = players[currentPlayerIndex];
    if (!current) return;

    const updatedPlayers = players.map((p, i) =>
      i === currentPlayerIndex
        ? { ...p, daresCompleted: p.daresCompleted + 1 }
        : p
    );

    const newCount = current.daresCompleted + 1;

    // Check win condition
    if (newCount >= config.targetDares) {
      set({
        players: updatedPlayers,
        phase: 'game_end',
        lastAction: 'completed',
      });
      return;
    }

    set({
      players: updatedPlayers,
      phase: 'result',
      lastAction: 'completed',
    });
  },

  dareSkipped: () => {
    const { players, currentPlayerIndex } = get();
    const current = players[currentPlayerIndex];
    if (!current) return;

    const updatedPlayers = players.map((p, i) =>
      i === currentPlayerIndex
        ? { ...p, daresDrunk: p.daresDrunk + 1 }
        : p
    );

    set({
      players: updatedPlayers,
      phase: 'result',
      lastAction: 'skipped',
    });
  },

  advancePlayer: () => {
    const { players, currentPlayerIndex, roundNumber } = get();
    const nextIndex = (currentPlayerIndex + 1) % players.length;
    const nextRound = nextIndex === 0 ? roundNumber + 1 : roundNumber;

    set({
      currentPlayerIndex: nextIndex,
      roundNumber: nextRound,
    });

    get().showNextDare();
  },

  resetGame: () => set(initialState),
}));
